import React, { useState, useEffect } from 'react';
import { Music, Mic2, Settings, Play, Pause, ExternalLink, ArrowLeft, Calendar, MapPin, Instagram, Music2, Edit, Share2, X, Check, Trash2, Upload } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Profile, getProfile, updateProfile, uploadProfileImage } from '../../lib/profile';
import { copyToClipboard } from '../../lib/utils';
import { getFollowerStats } from '../../lib/follow';
import { FollowButton } from './FollowButton';
import { MessageButton } from './MessageButton';

interface ProfilePageProps {
  profile: Profile;
  isOwnProfile?: boolean;
  onNavigateBack: () => void;
}

export function ProfilePage({ profile, isOwnProfile = false, onNavigateBack }: ProfilePageProps) {
  const [stats, setStats] = useState({ followers: 0, following: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState<Partial<Profile>>({});
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);

  useEffect(() => {
    loadStats();
  }, [profile.id]);

  useEffect(() => {
    if (profile) {
      setEditedProfile({
        bio: profile.bio || '',
        location: profile.location || '',
        instagram_url: profile.instagram_url || '',
        spotify_url: profile.spotify_url || '',
        tiktok_url: profile.tiktok_url || '',
        soundcloud_url: profile.soundcloud_url || '',
        artist_types: [...profile.artist_types]
      });
    }
  }, [profile]);

  const loadStats = async () => {
    try {
      const followerStats = await getFollowerStats(profile.id);
      setStats(followerStats);
    } catch (error) {
      console.error('Error loading stats:', error);
      setError('Fehler beim Laden der Statistiken');
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const updatedProfile = await updateProfile(profile.id, editedProfile);
      setIsEditing(false);
      // Reload the page to show updated profile
      window.location.reload();
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Fehler beim Aktualisieren des Profils');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedProfile({
      bio: profile.bio || '',
      location: profile.location || '',
      instagram_url: profile.instagram_url || '',
      spotify_url: profile.spotify_url || '',
      tiktok_url: profile.tiktok_url || '',
      soundcloud_url: profile.soundcloud_url || '',
      artist_types: [...profile.artist_types]
    });
    setIsEditing(false);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'avatar' | 'cover') => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    try {
      await uploadProfileImage(profile.id, file, type);
      // Reload the page to show updated images
      window.location.reload();
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Fehler beim Hochladen des Bildes');
    } finally {
      setLoading(false);
    }
  };

  const handleShareProfile = async () => {
    const url = `${window.location.origin}/u/${profile.username}`;
    await copyToClipboard(url);
    setShowCopiedMessage(true);
    setTimeout(() => setShowCopiedMessage(false), 2000);
  };

  return (
    <div className="flex-1 h-screen overflow-y-auto bg-background-primary">
      {/* Mobile Header */}
      <div className="md:hidden p-4 border-b border-divider">
        <button 
          onClick={onNavigateBack}
          className="p-2 hover:bg-background-secondary rounded-full"
        >
          <ArrowLeft size={20} />
        </button>
      </div>

      {/* Cover Image */}
      <div className="relative h-48 md:h-64 bg-background-secondary">
        <img
          src={profile.cover_image || "https://images.unsplash.com/photo-1511379938547-c1f69419868d"}
          alt="Cover"
          className="w-full h-full object-cover"
        />
        {isOwnProfile && isEditing && (
          <div className="absolute top-4 right-4 flex gap-2">
            <label className="p-2 bg-background-secondary hover:bg-background-tertiary rounded-full cursor-pointer text-text-primary">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleImageUpload(e, 'cover')}
              />
              <Upload size={20} />
            </label>
            {profile.cover_image && (
              <button
                onClick={() => handleImageUpload(new File([''], 'empty.txt'), 'cover')}
                className="p-2 bg-background-secondary hover:bg-background-tertiary rounded-full text-red-500"
              >
                <Trash2 size={20} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Profile Info Container */}
      <div className="relative px-4 md:px-8 pb-4">
        {/* Profile Image and Action Button */}
        <div className="flex justify-between items-start -mt-16">
          <div className="relative w-32 h-32 rounded-full border-4 border-background-primary overflow-hidden group">
            <img
              src={profile.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${profile.username}`}
              alt="Profile"
              className="w-full h-full object-cover"
            />
            {isOwnProfile && isEditing && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <label className="cursor-pointer p-2 hover:bg-background-tertiary rounded-full">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e, 'avatar')}
                  />
                  <Upload size={20} className="text-white" />
                </label>
                {profile.avatar_url && (
                  <button
                    onClick={() => handleImageUpload(new File([''], 'empty.txt'), 'avatar')}
                    className="p-2 hover:bg-background-tertiary rounded-full text-red-500"
                  >
                    <Trash2 size={20} />
                  </button>
                )}
              </div>
            )}
          </div>

          {isOwnProfile ? (
            <div className="flex gap-2 mt-20">
              <button
                onClick={() => isEditing ? handleSaveProfile() : setIsEditing(true)}
                className={cn(
                  "px-4 py-2 rounded-full font-medium flex items-center gap-2",
                  isEditing 
                    ? "bg-accent-primary hover:bg-accent-hover text-white"
                    : "bg-background-secondary hover:bg-background-tertiary"
                )}
                disabled={loading}
              >
                {isEditing ? (
                  <>
                    <Check size={18} />
                    Speichern
                  </>
                ) : (
                  <>
                    <Edit size={18} />
                    Profil bearbeiten
                  </>
                )}
              </button>
              {isEditing && (
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 bg-background-secondary hover:bg-background-tertiary rounded-full font-medium flex items-center gap-2"
                >
                  <X size={18} />
                  Abbrechen
                </button>
              )}
            </div>
          ) : (
            <div className="flex gap-2 mt-20">
              <FollowButton userId={profile.id} />
              <MessageButton userId={profile.id} username={profile.username} />
            </div>
          )}
        </div>

        {/* Profile Info */}
        <div className="mt-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold">{profile.username}</h1>
            <p className="text-text-secondary">@{profile.username.toLowerCase()}</p>
          </div>

          {/* Bio */}
          {isOwnProfile && isEditing ? (
            <textarea
              value={editedProfile.bio || ''}
              onChange={(e) => setEditedProfile(prev => ({ ...prev, bio: e.target.value }))}
              placeholder="Erzähle etwas über dich..."
              className="mt-4 w-full p-2 bg-background-tertiary rounded-lg border border-divider focus:outline-none focus:border-accent-primary resize-none"
              rows={3}
            />
          ) : profile.bio && (
            <p className="mt-4 text-text-primary">{profile.bio}</p>
          )}

          {/* Meta Info */}
          <div className="flex flex-wrap gap-4 mt-4 text-text-secondary">
            {isOwnProfile && isEditing ? (
              <div className="flex items-center gap-2">
                <MapPin size={16} />
                <input
                  type="text"
                  value={editedProfile.location || ''}
                  onChange={(e) => setEditedProfile(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Standort"
                  className="bg-background-tertiary rounded p-1 border border-divider focus:outline-none focus:border-accent-primary"
                />
              </div>
            ) : profile.location && (
              <div className="flex items-center gap-1">
                <MapPin size={16} />
                <span>{profile.location}</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <Calendar size={16} />
              <span>Beigetreten {new Date(profile.created_at).toLocaleDateString('de-DE', {
                month: 'long',
                year: 'numeric'
              })}</span>
            </div>
          </div>

          {/* Artist Types */}
          {profile.artist_types.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {profile.artist_types.map((type) => (
                <span
                  key={type}
                  className="px-3 py-1 bg-background-secondary rounded-full text-sm text-text-secondary"
                >
                  {type}
                </span>
              ))}
            </div>
          )}

          {/* Social Links */}
          <div className={cn("flex gap-4 mt-4", isEditing && "flex-col")}>
            {isOwnProfile && isEditing ? (
              <>
                <div className="flex items-center gap-2">
                  <Instagram size={20} className="text-text-secondary" />
                  <input
                    type="text"
                    value={editedProfile.instagram_url || ''}
                    onChange={(e) => setEditedProfile(prev => ({ ...prev, instagram_url: e.target.value }))}
                    placeholder="Instagram URL"
                    className="flex-1 bg-background-tertiary rounded p-2 border border-divider focus:outline-none focus:border-accent-primary"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Music2 size={20} className="text-text-secondary" />
                  <input
                    type="text"
                    value={editedProfile.spotify_url || ''}
                    onChange={(e) => setEditedProfile(prev => ({ ...prev, spotify_url: e.target.value }))}
                    placeholder="Spotify URL"
                    className="flex-1 bg-background-tertiary rounded p-2 border border-divider focus:outline-none focus:border-accent-primary"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-text-secondary">
                    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
                  </svg>
                  <input
                    type="text"
                    value={editedProfile.tiktok_url || ''}
                    onChange={(e) => setEditedProfile(prev => ({ ...prev, tiktok_url: e.target.value }))}
                    placeholder="TikTok URL"
                    className="flex-1 bg-background-tertiary rounded p-2 border border-divider focus:outline-none focus:border-accent-primary"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" className="text-text-secondary">
                    <path d="M5 12h2l1-3v6L7 12h2l1-3v6l-1-3h2l1-3v6l-1-3h2l1-3v6l-1-3h2l1-3v6l-1-3h2V9h4v6a3 3 0 0 1-3 3H5z"/>
                  </svg>
                  <input
                    type="text"
                    value={editedProfile.soundcloud_url || ''}
                    onChange={(e) => setEditedProfile(prev => ({ ...prev, soundcloud_url: e.target.value }))}
                    placeholder="SoundCloud URL"
                    className="flex-1 bg-background-tertiary rounded p-2 border border-divider focus:outline-none focus:border-accent-primary"
                  />
                </div>
              </>
            ) : (
              <>
                {profile.instagram_url && (
                  <a
                    href={profile.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-text-secondary hover:text-text-primary"
                  >
                    <Instagram size={20} />
                  </a>
                )}
                {profile.spotify_url && (
                  <a
                    href={profile.spotify_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-text-secondary hover:text-text-primary"
                  >
                    <Music2 size={20} />
                  </a>
                )}
                {profile.tiktok_url && (
                  <a
                    href={profile.tiktok_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-text-secondary hover:text-text-primary"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
                    </svg>
                  </a>
                )}
                {profile.soundcloud_url && (
                  <a
                    href={profile.soundcloud_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-text-secondary hover:text-text-primary"
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none">
                      <path d="M5 12h2l1-3v6L7 12h2l1-3v6l-1-3h2l1-3v6l-1-3h2l1-3v6l-1-3h2l1-3v6l-1-3h2V9h4v6a3 3 0 0 1-3 3H5z"/>
                    </svg>
                  </a>
                )}
              </>
            )}
          </div>

          {/* Stats */}
          <div className="flex gap-6 mt-4">
            <button className="hover:text-accent-primary">
              <span className="font-bold">{stats.followers + stats.following}</span>
              <span className="text-text-secondary ml-1">Freunde</span>
            </button>
          </div>
          
          {/* Share Profile Button */}
          {isOwnProfile && (
            <button
              onClick={handleShareProfile}
              className="mt-4 px-4 py-2 bg-background-secondary hover:bg-background-tertiary text-text-primary rounded-full font-medium flex items-center gap-2"
            >
              <Share2 size={18} />
              Profil teilen
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      {(error || showCopiedMessage) && (
        <div className="fixed bottom-4 right-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-500 px-4 py-2 rounded-lg">
              {error}
            </div>
          )}
          {showCopiedMessage && (
            <div className="bg-accent-primary/90 text-white px-4 py-2 rounded-lg">
              Link kopiert!
            </div>
          )}
        </div>
      )}
    </div>
  );
}